import type { PlaybookId } from "@repo/app-types/entities/playbook";
import type { RecordingId } from "@repo/app-types/entities/recording";
import type {
  RecordingAssessmentFeedback,
  RecordingAssessmentFeedbackSubmission,
} from "@repo/app-types/entities/recordingAssessmentFeedback";
import type { AdminApiRecordingToolActivity } from "@repo/app-types/entities/recordingToolActivity";
import {
  TagType,
  getTagsInvalidatedByCreate,
  getTagsInvalidatedByUpdate,
  getTagsProvidedByList,
} from "../helpers/tags.js";
import { api } from "../redux.js";

const recordingApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // Recording assessment feedback
    myRecordingAssessmentFeedback: builder.query<
      readonly RecordingAssessmentFeedback[],
      { recordingId: RecordingId; playbookId: PlaybookId },
      { data: { feedback: readonly RecordingAssessmentFeedback[] } }
    >({
      query: ({ recordingId, playbookId }) => {
        return {
          method: "GET",
          url: `/recordings/${encodeURIComponent(recordingId)}/feedback`,
          params: {
            playbook_id: playbookId,
          },
        };
      },
      transformResponse: (response) => response.data.feedback,
      providesTags: (result, _error, { recordingId }) =>
        getTagsProvidedByList(TagType.RecordingAssessmentFeedback, result, {
          filteredBy: [recordingId],
        }),
    }),

    postRecordingAssessmentFeedback: builder.mutation<
      void,
      {
        recordingId: RecordingId;
        playbookId: PlaybookId;
        data: RecordingAssessmentFeedbackSubmission;
      }
    >({
      query: ({ recordingId, playbookId, data }) => ({
        method: "POST",
        url: `/recordings/${encodeURIComponent(recordingId)}/feedback`,
        body: {
          recording_assessment_template_id: playbookId,
          data,
        },
      }),
      invalidatesTags: (_result, _error, { recordingId, playbookId }) =>
        getTagsInvalidatedByCreate(TagType.RecordingAssessmentFeedback, {
          inLists: [
            { filteredBy: [recordingId] },
            { filteredBy: [playbookId] },
          ],
        }),
    }),

    updateRecordingAssessmentFeedback: builder.mutation<
      void,
      {
        recordingId: RecordingId;
        playbookId: PlaybookId;
        feedbackId: string;
        data: RecordingAssessmentFeedbackSubmission;
      }
    >({
      query: ({ recordingId, feedbackId, data }) => ({
        method: "PUT",
        url: `/recordings/${encodeURIComponent(recordingId)}/feedback/${encodeURIComponent(feedbackId)}`,
        body: {
          data,
        },
      }),
      invalidatesTags: (
        _result,
        _error,
        { recordingId, playbookId, feedbackId },
      ) =>
        getTagsInvalidatedByUpdate(
          TagType.RecordingAssessmentFeedback,
          feedbackId,
          {
            inLists: [
              { filteredBy: [recordingId] },
              { filteredBy: [playbookId] },
            ],
          },
        ),
    }),

    generateAudioUrls: builder.query<
      readonly { id: string; audio_url: string }[],
      readonly string[],
      { data: { audio_urls: readonly { id: string; audio_url: string }[] } }
    >({
      query: (recordingIds) => ({
        method: "POST",
        url: "/recordings/generate-audio-urls",
        body: {
          ids: recordingIds,
        },
      }),
      transformResponse: (response) => response.data.audio_urls,
    }),

    recordingToolActivities: builder.query<
      Record<string, readonly AdminApiRecordingToolActivity[]>,
      readonly string[],
      {
        data: {
          tool_activities_by_recording_id: Record<
            string,
            readonly AdminApiRecordingToolActivity[]
          >;
        };
      }
    >({
      query: (recordingIds) => ({
        method: "POST",
        url: "/recordings/tool-activities",
        body: { recording_ids: recordingIds },
      }),
      transformResponse: (response) =>
        response.data.tool_activities_by_recording_id,
    }),
  }),
});

export const {
  useMyRecordingAssessmentFeedbackQuery,
  usePostRecordingAssessmentFeedbackMutation,
  useUpdateRecordingAssessmentFeedbackMutation,
  useGenerateAudioUrlsQuery,
  useRecordingToolActivitiesQuery,
} = recordingApi;

/**
 * Presigned audio URLs expire after 1 hour (see `createLongLivedPresignedGetUrl`),
 * so refetch them slightly before that on long-open pages. The audio player picks
 * up a replaced URL for the current source automatically, preserving the playback
 * position.
 */
export const AUDIO_URL_REFRESH_INTERVAL_MS = 50 * 60 * 1000;
